import { useState, type FormEvent } from "react";
import { type AdminUserDto } from "@mmm/contracts";
import { Button, EmptyState, ErrorState, Input, useT } from "@mmm/ui";
import { grantCreditTopUp } from "../../api/commands.js";
import { listUsers } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";
import { ListRow } from "../molecules/ListRow.js";
import { Modal } from "../shell/Modal.js";
import { SearchInput } from "../ui/SearchInput.js";

export function CreditTopUpPicker({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<AdminUserDto | null>(null);
  const [amount, setAmount] = useState(10);
  const [reason, setReason] = useState("");
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const users = useQuery(listUsers, { search: search.trim() || undefined });

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await grantCreditTopUp({ userId: selected.id, amount, reason: reason.trim(), requestId });
      setSuccess(true);
      setRequestId(crypto.randomUUID());
    } catch { /* The shared command layer shows the error; keep the same retry key. */ }
    finally { setSubmitting(false); }
  }

  return <Modal onClose={submitting ? undefined : onClose} width={520}>
    <h2>{t("credits.addCredits")}</h2>
    {success && <p role="status">{t("credits.topUpSaved")}</p>}
    {!selected ? <>
      <SearchInput value={search} onChange={setSearch} />
      <div className="mmm-admin-user-list">
        {users.error ? <ErrorState variant="list" title={t("admin.usersLoadFailed")} onRetry={users.retry} />
          : users.data?.length === 0 ? <EmptyState variant="list" title={t("admin.noUsersFound")} message={t("admin.noUsersFoundHint")} />
            : users.data?.map((user) => <ListRow key={user.id} title={user.displayName} meta={user.email}
              trailing={<Button variant="secondary" onClick={() => { setSelected(user); setSuccess(false); setRequestId(crypto.randomUUID()); }}>{t("credits.selectAccount")}</Button>} />)}
      </div>
    </> : <form onSubmit={submit}>
      <ListRow title={selected.displayName} meta={selected.email} trailing={<Button variant="secondary" onClick={() => { setSelected(null); setSuccess(false); }}>{t("credits.changeAccount")}</Button>} />
      <label className="mmm-field-label" htmlFor="top-up-amount">{t("credits.amount")}</label>
      <Input id="top-up-amount" type="number" min="1" max="100000" required value={amount} onChange={(event) => { setAmount(Number(event.target.value)); setRequestId(crypto.randomUUID()); setSuccess(false); }} />
      <label className="mmm-field-label" htmlFor="top-up-reason">{t("credits.reason")}</label>
      <Input id="top-up-reason" required minLength={5} maxLength={500} value={reason} onChange={(event) => { setReason(event.target.value); setRequestId(crypto.randomUUID()); setSuccess(false); }} />
      <div className="mmm-modal-actions"><Button variant="primary" type="submit" disabled={submitting || success}>{t("credits.addCredits")}</Button></div>
    </form>}
    <div className="mmm-modal-actions"><Button variant="secondary" onClick={onClose}>{t("common.close")}</Button></div>
  </Modal>;
}
